/**
 * Hook centralisé pour TOUTE modification de feuilles d'heures.
 *
 * Tout passage par ce hook garantit :
 * - Audit log dans `payroll_corrections` (qui, quoi, quand, pourquoi)
 * - Notification cross-rôle (admin/secrétariat/employé selon l'action)
 * - Toast de feedback utilisateur
 * - Validation du motif obligatoire pour les corrections post-validation
 *
 * Workflow Option A enrichie :
 *   draft → pending (employé)
 *   pending → validated|rejected (admin via validateSheet/rejectSheet)
 *   validated → corrigé (secrétariat via editTimeEntry avec actorPage='secretariat_payroll')
 */
import { useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from './useToast';
import {
  computeSheetStatus,
  validateMonthInDayStatuses,
  validateAllSubmittedDays,
  formatMonthFr,
  type DayStatuses,
} from '../utils/sheetStatusUtils';

export type ActorPage =
  | 'admin_validate'
  | 'secretariat_payroll'
  | 'secretariat_validate'
  | 'timesheet_employee';

export interface EditOptions {
  /** ID de la feuille hebdomadaire à laquelle se rattache l'audit */
  sheetId: string;
  /** Page d'origine de l'action (pour traçabilité fine) */
  actorPage: ActorPage;
  /** Motif (OBLIGATOIRE si la feuille est `validated` et que l'auteur est secrétariat) */
  reason?: string;
}

interface AuditEntry {
  time_entry_id: string | null;
  weekly_sheet_id: string | null;
  leave_id?: string | null;
  corrected_by: string;
  actor_role: string;
  actor_page: string;
  action: 'edit' | 'correct' | 'validate' | 'reject' | 'delete';
  record_type: 'time_entry' | 'weekly_sheet' | 'leave';
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  reason: string | null;
}

export function useTimesheetEdit() {
  const { user } = useAuth();
  const { toast } = useToast();

  /** Récupère le statut courant + l'employee_id + day_statuses propriétaire d'une feuille */
  const getSheetContext = useCallback(async (sheetId: string) => {
    const { data, error } = await supabase
      .from('weekly_sheets')
      .select('id, status, employee_id, day_statuses, rejection_reason')
      .eq('id', sheetId)
      .single();
    if (error || !data) return null;
    return data as {
      id: string;
      status: string;
      employee_id: string;
      day_statuses: DayStatuses | null;
      rejection_reason: string | null;
    };
  }, []);

  /**
   * Insère une notification persistante pour un destinataire.
   * `sheetId` peut être null pour les événements qui ne sont pas liés à
   * une feuille hebdo (ex: suppression de congé).
   */
  const notify = useCallback(async (
    recipientId: string,
    type: 'sheet_validated' | 'sheet_rejected' | 'sheet_corrected',
    title: string,
    message: string,
    sheetId: string | null,
    relatedEmployeeId?: string,
  ) => {
    await supabase.from('notifications').insert({
      recipient_id: recipientId,
      type,
      title,
      message,
      related_sheet_id: sheetId,
      related_employee_id: relatedEmployeeId ?? null,
    });
  }, []);

  /** Notifie TOUS les admins (utilisé pour les corrections post-validation) */
  const notifyAllAdmins = useCallback(async (
    type: 'sheet_corrected',
    title: string,
    message: string,
    sheetId: string,
    employeeId: string,
  ) => {
    const { data: admins } = await supabase
      .from('employees')
      .select('id')
      .eq('role', 'admin')
      .eq('is_active', true);

    if (!admins) return;
    await Promise.all(admins.map(a =>
      notify(a.id, type, title, message, sheetId, employeeId)
    ));
  }, [notify]);

  /** Insère N entrées d'audit en une seule requête */
  const insertAudit = useCallback(async (entries: AuditEntry[]) => {
    if (entries.length === 0) return;
    await supabase.from('payroll_corrections').insert(entries);
  }, []);

  /**
   * Édite UN time_entry. Trace chaque champ modifié dans l'audit log.
   * Si la feuille est `validated`, applique le workflow de correction
   * (motif obligatoire + notification admins).
   */
  const editTimeEntry = useCallback(async (
    entryId: string,
    fields: Record<string, unknown>,
    options: EditOptions,
  ): Promise<boolean> => {
    if (!user) {
      toast.error('Utilisateur non authentifié.');
      return false;
    }

    const sheet = await getSheetContext(options.sheetId);
    if (!sheet) {
      toast.error('Feuille introuvable.');
      return false;
    }

    const isPostValidation = sheet.status === 'validated';
    const isCorrection = isPostValidation && options.actorPage === 'secretariat_payroll';

    if (isCorrection && !options.reason?.trim()) {
      toast.warning('Un motif est requis pour corriger une feuille validée.');
      return false;
    }

    // Lire les valeurs actuelles pour journaliser old → new
    const { data: currentEntry, error: fetchErr } = await supabase
      .from('time_entries')
      .select('*')
      .eq('id', entryId)
      .single();

    if (fetchErr || !currentEntry) {
      toast.error('Saisie introuvable.');
      return false;
    }

    // Appliquer la mise à jour
    const { error: updateErr } = await supabase
      .from('time_entries')
      .update(fields)
      .eq('id', entryId);

    if (updateErr) {
      console.error('[useTimesheetEdit] editTimeEntry update error:', updateErr);
      toast.error(`Erreur lors de la modification : ${updateErr.message}`);
      return false;
    }

    // Construire les entrées d'audit (une par champ modifié)
    const auditEntries: AuditEntry[] = Object.entries(fields).map(([field, newVal]) => ({
      time_entry_id: entryId,
      weekly_sheet_id: options.sheetId,
      corrected_by: user.id,
      actor_role: user.role,
      actor_page: options.actorPage,
      action: isCorrection ? 'correct' : 'edit',
      record_type: 'time_entry',
      field_name: field,
      old_value: currentEntry[field] != null ? String(currentEntry[field]) : null,
      new_value: newVal != null ? String(newVal) : null,
      reason: options.reason ?? null,
    }));

    await insertAudit(auditEntries);

    // Notifier les admins en cas de correction post-validation
    if (isCorrection) {
      await notifyAllAdmins(
        'sheet_corrected',
        'Feuille corrigée par le secrétariat',
        `Une feuille validée a été corrigée${options.reason ? ` : ${options.reason}` : ''}.`,
        options.sheetId,
        sheet.employee_id,
      );
    }

    return true;
  }, [user, toast, getSheetContext, insertAudit, notifyAllAdmins]);

  /**
   * Édite la weekly_sheet (override_weekly_hours, etc. — hors changement de statut).
   * Pour valider/rejeter, utiliser validateSheet/rejectSheet.
   */
  const editWeeklySheet = useCallback(async (
    sheetId: string,
    fields: Record<string, unknown>,
    options: Omit<EditOptions, 'sheetId'>,
  ): Promise<boolean> => {
    if (!user) {
      toast.error('Utilisateur non authentifié.');
      return false;
    }

    const sheet = await getSheetContext(sheetId);
    if (!sheet) {
      toast.error('Feuille introuvable.');
      return false;
    }

    const { data: currentSheet, error: fetchErr } = await supabase
      .from('weekly_sheets')
      .select('*')
      .eq('id', sheetId)
      .single();

    if (fetchErr || !currentSheet) {
      toast.error('Feuille introuvable.');
      return false;
    }

    const { error } = await supabase
      .from('weekly_sheets')
      .update(fields)
      .eq('id', sheetId);

    if (error) {
      console.error('[useTimesheetEdit] editWeeklySheet error:', error);
      toast.error(`Erreur : ${error.message}`);
      return false;
    }

    const isPostValidation = sheet.status === 'validated';
    const isCorrection = isPostValidation && options.actorPage === 'secretariat_payroll';

    const auditEntries: AuditEntry[] = Object.entries(fields).map(([field, newVal]) => ({
      time_entry_id: null,
      weekly_sheet_id: sheetId,
      corrected_by: user.id,
      actor_role: user.role,
      actor_page: options.actorPage,
      action: isCorrection ? 'correct' : 'edit',
      record_type: 'weekly_sheet',
      field_name: field,
      old_value: currentSheet[field] != null ? String(currentSheet[field]) : null,
      new_value: newVal != null ? String(newVal) : null,
      reason: options.reason ?? null,
    }));

    await insertAudit(auditEntries);

    if (isCorrection) {
      await notifyAllAdmins(
        'sheet_corrected',
        'Feuille corrigée par le secrétariat',
        `La base horaire ou un autre paramètre de feuille a été corrigé.`,
        sheetId,
        sheet.employee_id,
      );
    }

    return true;
  }, [user, toast, getSheetContext, insertAudit, notifyAllAdmins]);

  /**
   * Valide une feuille ou un mois précis d'une feuille (semaine à cheval).
   *
   * - `monthKey` absent ou null : valide TOUS les jours 'submitted' → 'validated'.
   *   Usage standard : feuille mono-mois ou bouton "Valider tout" sur cheval.
   * - `monthKey` fourni (ex: '2026-03') : valide uniquement les jours de ce mois
   *   qui sont 'submitted' ou déjà 'validated'. Les autres jours (ex: avril draft)
   *   restent inchangés. Usage : bouton "Valider mars" sur semaine à cheval.
   *
   * Le `status` global de la feuille est dérivé (via computeSheetStatus) et reflète :
   *   - 'pending' tant qu'il reste un jour 'submitted' OU un jour 'draft' à côté
   *     de jours 'validated' (cheval partielle en cours)
   *   - 'validated' uniquement si TOUS les jours sont 'validated' (feuille figée)
   *
   * Ainsi valider uniquement mars d'une S14 à cheval ne fige pas la feuille et
   * l'employé peut toujours saisir avril (pas de blocage RLS).
   */
  const validateSheet = useCallback(async (
    sheetId: string,
    actorPage: ActorPage = 'admin_validate',
    monthKey?: string | null,
  ): Promise<boolean> => {
    if (!user) return false;

    const sheet = await getSheetContext(sheetId);
    if (!sheet) {
      toast.error('Feuille introuvable.');
      return false;
    }

    const currentDayStatuses: DayStatuses = (sheet.day_statuses ?? {}) as DayStatuses;
    const newDayStatuses = monthKey
      ? validateMonthInDayStatuses(currentDayStatuses, monthKey)
      : validateAllSubmittedDays(currentDayStatuses);

    // Garde-fou : si aucun jour n'a changé, ne rien faire (évite un UPDATE inutile
    // + audit log spurious). Exemple : admin revalide mars alors que mars est déjà
    // validated → no-op avec message.
    const changed = Object.entries(newDayStatuses).some(
      ([k, v]) => currentDayStatuses[k] !== v,
    );
    if (!changed) {
      toast.info(
        monthKey
          ? `Les jours de ${formatMonthFr(monthKey)} sont déjà validés.`
          : 'Aucun jour soumis à valider.',
      );
      return true;
    }

    const newStatus = computeSheetStatus(newDayStatuses, sheet.rejection_reason);

    // validated_at est maintenu automatiquement par le trigger DB
    // set_weekly_sheet_validated_at (migration 022). validated_by trace QUI a validé.
    const { data: updated, error } = await supabase
      .from('weekly_sheets')
      .update({
        status: newStatus,
        day_statuses: newDayStatuses,
        validated_by: user.id,
      })
      .eq('id', sheetId)
      .select('id');

    if (error) {
      console.error('[useTimesheetEdit] validateSheet error:', error);
      toast.error(`Erreur lors de la validation : ${error.message}`);
      return false;
    }
    if (!updated || updated.length === 0) {
      toast.error(
        'Validation refusée par la sécurité (RLS). La feuille est peut-être déjà figée.',
      );
      return false;
    }

    await insertAudit([{
      time_entry_id: null,
      weekly_sheet_id: sheetId,
      corrected_by: user.id,
      actor_role: user.role,
      actor_page: actorPage,
      action: 'validate',
      record_type: 'weekly_sheet',
      field_name: monthKey ? `day_statuses[${monthKey}]` : 'day_statuses',
      old_value: JSON.stringify(currentDayStatuses),
      new_value: JSON.stringify(newDayStatuses),
      reason: null,
    }]);

    // Notifier l'employé
    const notifMsg = monthKey
      ? `Les heures de ${formatMonthFr(monthKey)} de votre feuille d'heures ont été validées.`
      : "Votre feuille d'heures a été validée par la direction.";
    await notify(
      sheet.employee_id,
      'sheet_validated',
      'Feuille validée',
      notifMsg,
      sheetId,
    );

    toast.success(monthKey ? `${formatMonthFr(monthKey)} validé.` : 'Feuille validée.');
    return true;
  }, [user, toast, getSheetContext, insertAudit, notify]);

  /**
   * Rejette une feuille (pending → rejected) avec motif + notifie l'employé.
   */
  const rejectSheet = useCallback(async (
    sheetId: string,
    reason: string,
    actorPage: ActorPage = 'admin_validate',
  ): Promise<boolean> => {
    if (!user) return false;
    if (!reason.trim()) {
      toast.warning('Un motif de rejet est requis.');
      return false;
    }

    const sheet = await getSheetContext(sheetId);
    if (!sheet) {
      toast.error('Feuille introuvable.');
      return false;
    }

    // rejected_by est une colonne Oger-specific qui trace QUI a rejeté.
    const { error } = await supabase
      .from('weekly_sheets')
      .update({
        status: 'rejected',
        rejection_reason: reason,
        rejected_at: new Date().toISOString(),
        rejected_by: user.id,
      })
      .eq('id', sheetId);

    if (error) {
      console.error('[useTimesheetEdit] rejectSheet error:', error);
      toast.error(`Erreur lors du rejet : ${error.message}`);
      return false;
    }

    await insertAudit([{
      time_entry_id: null,
      weekly_sheet_id: sheetId,
      corrected_by: user.id,
      actor_role: user.role,
      actor_page: actorPage,
      action: 'reject',
      record_type: 'weekly_sheet',
      field_name: 'status',
      old_value: sheet.status,
      new_value: 'rejected',
      reason,
    }]);

    await notify(
      sheet.employee_id,
      'sheet_rejected',
      'Feuille rejetée',
      `Votre feuille d'heures a été rejetée. Motif : ${reason}`,
      sheetId,
    );

    toast.success('Feuille rejetée. L\'employé a été notifié.');
    return true;
  }, [user, toast, getSheetContext, insertAudit, notify]);

  /**
   * Supprime un congé + log d'audit + notification employé si post-validation.
   * - Pour un congé `validated`, un motif est OBLIGATOIRE (traçabilité).
   * - L'entrée d'audit survit à la suppression grâce à la FK
   *   `leave_id ... ON DELETE SET NULL` (migration 023).
   */
  const deleteLeave = useCallback(async (
    leaveId: string,
    options: { reason?: string; actorPage?: ActorPage } = {},
  ): Promise<boolean> => {
    if (!user) {
      toast.error('Utilisateur non authentifié.');
      return false;
    }

    // Lire le congé AVANT suppression pour capturer old_value + notifier
    const { data: leave, error: fetchErr } = await supabase
      .from('leaves')
      .select('id, status, type, start_date, end_date, employee_id')
      .eq('id', leaveId)
      .single();

    if (fetchErr || !leave) {
      toast.error('Congé introuvable.');
      return false;
    }

    const isValidated = leave.status === 'validated';
    if (isValidated && !options.reason?.trim()) {
      toast.warning('Un motif est requis pour supprimer un congé validé.');
      return false;
    }

    // Ordre : on tente la suppression d'abord. Si elle échoue, on n'écrit
    // aucun audit ni notification (pas d'audit orphelin, pas de fausse alerte
    // envoyée à l'employé). Si elle réussit, on log + notifie.
    const { error: delErr } = await supabase
      .from('leaves')
      .delete()
      .eq('id', leaveId);

    if (delErr) {
      console.error('[useTimesheetEdit] deleteLeave error:', delErr);
      toast.error(`Erreur lors de la suppression : ${delErr.message}`);
      return false;
    }

    // Audit post-suppression. La FK `leave_id ... ON DELETE SET NULL`
    // (migration 023) accepte une référence vers un leave qui n'existe plus :
    // PostgreSQL ne valide pas la FK à l'insertion si on met NULL, donc on
    // inscrit NULL tout en gardant `old_value`, `reason` et `record_type`
    // pour retrouver la trace par date + actor.
    await insertAudit([{
      time_entry_id: null,
      weekly_sheet_id: null,
      leave_id: null,
      corrected_by: user.id,
      actor_role: user.role,
      actor_page: options.actorPage ?? 'secretariat_payroll',
      action: 'delete',
      record_type: 'leave',
      field_name: 'status',
      old_value: leave.status,
      new_value: 'deleted',
      reason: options.reason ?? null,
    }]);

    // Notifier l'employé si son congé validé a été supprimé
    if (isValidated) {
      await notify(
        leave.employee_id,
        'sheet_corrected',
        'Congé supprimé',
        `Votre congé du ${leave.start_date} au ${leave.end_date} a été supprimé${
          options.reason ? ` — motif : ${options.reason}` : ''
        }.`,
        null, // pas de weekly_sheet associée à un congé
        leave.employee_id,
      );
    }

    toast.success('Congé supprimé.');
    return true;
  }, [user, toast, insertAudit, notify]);

  return {
    editTimeEntry,
    editWeeklySheet,
    validateSheet,
    rejectSheet,
    deleteLeave,
  };
}
